import { Link } from "react-router-dom";
import { ArrowRight, FileText } from "lucide-react";

import { SiteLayout } from "@/components/site/SiteLayout";
import { PageSEO } from "@/components/site/PageSEO";
import { CTASection } from "@/components/site/CTASection";
import { ResourceHero } from "@/components/site/ResourceHero";
import { irsForms } from "@/data/site/irsForms";
import { stockImages } from "@/data/site/stockImages";
import { Reveal } from "@/components/motion/Reveal";

const IRSForms = () => (
  <SiteLayout>
    <PageSEO
      title="IRS Form Library — Tax Resolution Forms Explained"
      description="Plain-language guide to the IRS forms used in tax resolution: Form 656, 433-A, 2848, 9465, 12153, 8857, and more."
      path="/resources/irs-forms"
      image={stockImages.signingForm.src}
    />
    <ResourceHero
      breadcrumbs={[
        { name: "Home", href: "/" },
        { name: "Resources", href: "/resources" },
        { name: "IRS Forms" },
      ]}
      eyebrow="Form library"
      title="IRS forms used in tax resolution"
      summary={`Every important IRS form a taxpayer or representative files during a resolution case — what it does, who files it, when it's used, and a link to the official PDF. ${irsForms.length} forms covered.`}
      facts={[
        { label: "Forms covered", value: irsForms.length },
        { label: "Source", value: "IRS.gov" },
        { label: "Last reviewed", value: "2025" },
      ]}
      background="signingForm"
    />


    <section className="py-12 lg:py-16 bg-white">
      <div className="container mx-auto px-4 max-w-5xl divide-y divide-border">
        {irsForms.map((f, i) => (
          <Reveal key={f.id} delay={Math.min(i, 4) * 60}>
            <article id={f.id} className="py-12 first:pt-0 last:pb-0 grid md:grid-cols-[180px_1fr] gap-8 md:gap-10 scroll-mt-24">
            <div className="order-first">
              {f.image ? (
                <Link
                  to={`/resources/irs-forms/${f.id}`}
                  className="block group"
                  aria-label={`Read full guide for IRS ${f.number}`}
                >
                  <img
                    src={f.image}
                    alt={`First page of IRS ${f.number} — ${f.title}`}
                    loading="lazy"
                    width={180}
                    height={233}
                    className="w-full h-auto border border-border shadow-sm group-hover:shadow-md transition-shadow bg-white"
                  />
                </Link>
              ) : (
                <div className="w-full aspect-[8.5/11] border border-border bg-secondary flex items-center justify-center text-muted-foreground">
                  <FileText className="w-10 h-10" />
                </div>
              )}
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.15em] text-gold mb-2">
                Form {f.number}
              </p>
              <h2 className="font-heading text-2xl font-bold text-navy mb-5 leading-[1.2]">
                <Link to={`/resources/irs-forms/${f.id}`} className="hover:text-gold transition-colors">
                  {f.title}
                </Link>
              </h2>
              <dl className="space-y-3 text-[15px]">
                <Row label="Purpose" value={f.purpose} />
                <Row label="Who files" value={f.whoFiles} />
                <Row label="When used" value={f.whenUsed} />
              </dl>
              <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2">
                <Link
                  to={`/resources/irs-forms/${f.id}`}
                  aria-label={`Read full guide for IRS Form ${f.number} — ${f.title}`}
                  className="text-sm font-bold text-gold hover:text-gold-dark inline-flex items-center gap-1"
                >
                  Read full guide <ArrowRight className="w-3.5 h-3.5" aria-hidden="true" />
                </Link>
                {f.pdfUrl && (
                  <a
                    href={f.pdfUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Open official IRS ${f.image ? "PDF" : "page"} for Form ${f.number} (opens in new tab)`}
                    className="text-sm font-semibold text-navy hover:text-gold inline-flex items-center gap-1"
                  >
                    Official IRS {f.image ? "PDF" : "page"} <ArrowRight className="w-3.5 h-3.5" aria-hidden="true" />
                  </a>
                )}
                {f.relatedServices?.map((r) => (
                  <Link
                    key={r.href}
                    to={r.href}
                    className="text-sm font-semibold text-navy hover:text-gold inline-flex items-center gap-1"
                  >
                    {r.label} <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                ))}
              </div>
            </div>
            </article>
          </Reveal>
        ))}
      </div>
    </section>


    <CTASection />
  </SiteLayout>
);

const Row = ({ label, value }: { label: string; value: string }) => (
  <div className="grid sm:grid-cols-[120px_1fr] gap-1 sm:gap-6">
    <dt className="text-[11px] font-bold text-muted-foreground uppercase tracking-[0.15em] pt-1">
      {label}
    </dt>
    <dd className="text-foreground/85 leading-relaxed">{value}</dd>
  </div>
);

export default IRSForms;
